import { useMemo } from 'react'

import { EChart } from '@/components/charts/EChart'
import { ExportButton } from '@/components/data-table/ExportButton'
import { Empty } from '@/components/feedback/Empty'
import { ErrorState } from '@/components/feedback/ErrorState'
import { Loading } from '@/components/feedback/Loading'
import { StatCard } from '@/components/stat/StatCard'
import { FilterField } from '@/components/filters/FilterBar'
import { OptionSelect } from '@/components/filters/OptionSelect'
import { Card, CardContent } from '@/components/ui/card'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { buildTimeSeriesOption } from '@/features/report/chart-option'
import { ReportFilterBar } from '@/features/report/ReportFilterBar'
import { useReportFilters } from '@/features/report/use-report-filters'
import { useCubeLiveQuery } from '@/hooks/use-cube-live-query'
import {
  exportFieldValue,
  formatFieldValue,
  GRANULARITIES,
  type ChartReport,
  type Granularity,
  type ReportField,
} from '@/lib/cube-report'
import { changePercent, formatInteger, formatTimeBucket } from '@/lib/format'
import { messages } from '@/lib/messages'
import { queryKeys } from '@/lib/query-keys'
import { cn } from '@/lib/utils'
import {
  bucketKey,
  chartSeriesQuery,
  chartSummaryQuery,
  toChartSummary,
  toRows,
} from '@/services/cube/report'

function parseGranularity(raw: string | undefined): Granularity | undefined {
  return GRANULARITIES.find((item) => item === raw)
}

/**
 * Zaman serisi raporu: özet kartları + grafik + altında grafiğin verisi. Kırılım
 * (gün/hafta/ay/...) ve önceki dönemle karşılaştırma Cube sorgusunda çözülür;
 * serilerin, eksenlerin ve özet ölçülerinin ne olduğu view meta'sından gelir.
 */
export function ChartReportView({ report }: { report: ChartReport }) {
  const filters = useReportFilters(report)
  const { state } = filters
  const granularity = parseGranularity(filters.get('granularity')) ?? report.granularity
  const canCompare = Boolean(state.dateFrom && state.dateTo)
  const compare = canCompare && filters.get('compare') === '1'

  const seriesQuery = useCubeLiveQuery({
    queryKey: queryKeys.cube(report.view, { series: granularity, filters: state }),
    query: chartSeriesQuery(report, state, granularity),
    // Önceki veri yeni kova anahtarıyla okunmasın: satırlar çekildikleri granülerlikle taşınır.
    select: (result) => ({ granularity, rows: toRows(result) }),
  })
  const summaryQuery = useCubeLiveQuery({
    queryKey: queryKeys.cube(report.view, { summary: compare, filters: state }),
    query: chartSummaryQuery(report, state, compare),
    select: toChartSummary,
  })

  const rows = useMemo(() => seriesQuery.data?.rows ?? [], [seriesQuery.data])
  const shown = seriesQuery.data?.granularity ?? granularity
  const option = useMemo(() => buildTimeSeriesOption(report, rows, shown), [report, rows, shown])
  const busy = seriesQuery.isPlaceholderData || summaryQuery.isPlaceholderData

  const isLoading = seriesQuery.isLoading || summaryQuery.isLoading
  const isError = seriesQuery.isError || summaryQuery.isError
  const timeField = report.fields.get(report.time) as ReportField
  const tableFields = report.table.map((member) => report.fields.get(member) as ReportField)
  const key = bucketKey(report, shown)

  const exportColumns = [
    { header: timeField.label },
    ...tableFields.map((field) => ({ header: field.label, numberFormat: field.numberFormat })),
  ]
  const exportRows = rows.map((row) => [
    formatTimeBucket(row[key] as string, shown),
    ...tableFields.map((field) => exportFieldValue(field, row[field.name])),
  ])

  return (
    <>
      <ReportFilterBar report={report} filters={filters}>
        <FilterField
          label={messages.report.granularity}
          htmlFor={`report-${report.view}-granularity`}
        >
          <OptionSelect
            id={`report-${report.view}-granularity`}
            className="w-44"
            value={granularity}
            options={GRANULARITIES.map((item) => ({
              value: item,
              label: messages.report.granularities[item],
            }))}
            onValueChange={(next) => filters.set({ granularity: next })}
          />
        </FilterField>
        <label
          className="flex h-(--control-height-md) items-center gap-2 text-sm"
          title={canCompare ? undefined : messages.report.compareNeedsRange}
        >
          <Checkbox
            checked={compare}
            disabled={!canCompare}
            onCheckedChange={(checked) =>
              filters.set({ compare: checked === true ? '1' : undefined })
            }
          />
          {messages.report.compare}
        </label>
      </ReportFilterBar>

      <Card
        aria-busy={busy || undefined}
        className={cn('transition-opacity', busy && 'opacity-60')}
      >
        <CardContent className="pt-4">
          {isLoading ? (
            <Loading rows={6} />
          ) : isError ? (
            <ErrorState
              error={seriesQuery.error ?? summaryQuery.error}
              onRetry={() => {
                void seriesQuery.refetch()
                void summaryQuery.refetch()
              }}
            />
          ) : rows.length === 0 ? (
            <Empty />
          ) : (
            <>
              <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
                <StatCard
                  label={messages.report.bucketCount[shown]}
                  value={formatInteger(rows.length)}
                />
                {report.summary.map((member) => {
                  const field = report.fields.get(member) as ReportField
                  const current = summaryQuery.data?.current[member]
                  const previous = summaryQuery.data?.previous?.[member]
                  const compared = summaryQuery.data?.previous !== undefined
                  return (
                    <StatCard
                      key={member}
                      label={field.label}
                      value={formatFieldValue(field, current)}
                      change={compared ? changePercent(current, previous) : undefined}
                      hint={
                        compared
                          ? messages.report.previousPeriod(formatFieldValue(field, previous))
                          : undefined
                      }
                    />
                  )
                })}
              </div>
              <EChart option={option} height={320} notMerge />
            </>
          )}
        </CardContent>
      </Card>

      {!isLoading && !isError && rows.length > 0 && tableFields.length > 0 && (
        <div
          aria-busy={busy || undefined}
          className={cn('border-border rounded-md border transition-opacity', busy && 'opacity-60')}
        >
          <div className="border-border flex items-center justify-between border-b p-2">
            <p className="px-1 text-sm font-medium">{messages.report.tableTitle}</p>
            <ExportButton
              filename={report.view.toLowerCase()}
              columns={exportColumns}
              rows={exportRows}
            />
          </div>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>{timeField.label}</TableHead>
                {tableFields.map((field) => (
                  <TableHead key={field.name} className="text-right">
                    {field.label}
                  </TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((row) => (
                <TableRow key={row[key] as string}>
                  <TableCell>{formatTimeBucket(row[key] as string, shown)}</TableCell>
                  {tableFields.map((field) => (
                    <TableCell key={field.name} className="tabular text-right">
                      {formatFieldValue(field, row[field.name])}
                    </TableCell>
                  ))}
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </>
  )
}
